import { colors, fonts, borderRadius, spacing } from './index';

// Animation timeouts
export const ANIMATION_TIMEOUTS = {
  FADE_IN: 800,
  ERROR_FADE: 300,
};

// Layout constants
export const LAYOUT = {
  MAX_FORM_WIDTH: '440px',
  BUTTON_MIN_HEIGHT: '44px',
  LOGO_HEIGHT: { xs: 44, md: 56 },
  LEFT_COLUMN_WIDTH: { xs: '100%', md: '60%' },
};

// Error alert colors
export const ERROR_ALERT_COLORS = {
  BACKGROUND: 'rgba(239, 68, 68, 0.08)',
  BORDER: 'rgba(239, 68, 68, 0.2)',
};

// Style constants
export const containerStyles = {
  display: 'flex',
  height: '100vh',
  overflow: 'hidden',
  backgroundColor: colors.background,
};

export const leftColumnStyles = {
  width: LAYOUT.LEFT_COLUMN_WIDTH,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: { xs: spacing.lg, md: spacing.xl },
  backgroundColor: colors.backgroundWhite,
  height: '100vh',
  overflow: 'auto',
  position: 'relative',
};

export const containerInnerStyles = {
  width: '100%',
  py: 0,
  px: { xs: spacing.md, md: spacing.lg },
};

export const formContainerStyles = {
  width: '100%',
  maxWidth: LAYOUT.MAX_FORM_WIDTH,
  margin: '0 auto',
  animation: 'fadeInUp 0.8s ease-out',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
};

export const logoBoxStyles = {
  display: 'flex',
  justifyContent: 'flex-start',
  mb: spacing.lg,
};

export const logoImageStyles = {
  height: LAYOUT.LOGO_HEIGHT,
  width: 'auto',
};

export const headerStyles = {
  textAlign: 'left',
  mb: spacing.xl,
};

export const headerTitleStyles = {
  fontSize: { xs: '28px', md: '32px' },
  fontWeight: fonts.weightBold,
  color: colors.headerColor,
  mb: spacing.sm,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
};

export const headerDescriptionStyles = {
  fontSize: fonts.descriptionSize,
  color: colors.textSecondary,
  fontWeight: fonts.weightNormal,
  lineHeight: 1.5,
};

export const errorAlertStyles = {
  borderRadius: borderRadius.md,
  py: spacing.xs,
  px: spacing.sm,
  fontSize: fonts.fontSizeSm,
  minHeight: 'auto',
  backgroundColor: ERROR_ALERT_COLORS.BACKGROUND,
  color: colors.error,
  border: `1px solid ${ERROR_ALERT_COLORS.BORDER}`,
  mb: spacing.md,
  '& .MuiAlert-message': {
    padding: 0,
    fontWeight: fonts.weightMedium,
  },
};

export const formStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
};

// Shared TextField styles (used for both email and password)
export const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: borderRadius.md,
    backgroundColor: colors.backgroundWhite,
    transition: 'all 0.3s ease',
    cursor: 'text',
    '& fieldset': {
      borderColor: colors.borderDefault,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: borderRadius.md,
      top: 0,
    },
    '&:hover': {
      cursor: 'text',
      '& fieldset': {
        borderColor: colors.borderHover,
        borderWidth: '1px',
        borderRadius: borderRadius.md,
      },
    },
    '&.Mui-focused': {
      boxShadow: 'none',
      cursor: 'text',
      '& fieldset': {
        borderColor: colors.borderFocus,
        borderWidth: '2px',
        borderRadius: borderRadius.md,
      },
    },
    '&.Mui-error': {
      '& fieldset': {
        borderColor: colors.error,
        borderRadius: borderRadius.md,
      },
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 12px',
    color: colors.textPrimary,
    fontWeight: fonts.weightNormal,
    fontSize: '12px',
    lineHeight: 1.4,
    cursor: 'text',
    '&:focus': {
      cursor: 'text',
    },
  },
};

export const passwordFieldWrapperStyles = {
  // No additional styles needed, just a wrapper Box
};

export const passwordToggleButtonStyles = {
  color: colors.textSecondary,
  '&:hover': {
    color: colors.textPrimary,
  },
};

export const rememberMeRowStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mt: spacing.sm,
};

export const checkboxStyles = {
  color: colors.borderDefault,
  '&.Mui-checked': {
    color: colors.primary,
  },
  padding: spacing.xs,
};

export const rememberMeLabelStyles = {
  fontSize: fonts.fontSizeSm,
  color: colors.textSecondary,
  fontWeight: fonts.weightNormal,
};

export const forgotPasswordLinkStyles = {
  color: colors.primary,
  textDecoration: 'none',
  fontSize: fonts.fontSizeSm,
  fontWeight: fonts.weightNormal,
  transition: 'color 0.2s ease',
};

export const loginButtonStyles = {
  mt: spacing.sm,
  py: spacing.sm,
  minHeight: LAYOUT.BUTTON_MIN_HEIGHT,
  borderRadius: borderRadius.md,
  background: colors.primaryGradient,
  color: colors.textWhite || '#ffffff',
  textTransform: 'none',
  fontSize: fonts.descriptionSize,
  fontWeight: fonts.weightSemiBold,
  letterSpacing: fonts.letterSpacingNormal,
  boxShadow: `0 12px 24px ${colors.primaryShadow}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: colors.primaryGradientHover,
    boxShadow: `0 16px 32px ${colors.primaryShadow}`,
    transform: 'translateY(-2px)',
    color: colors.textWhite || '#ffffff',
  },
  '&:active': {
    transform: 'translateY(0)',
    color: colors.textWhite || '#ffffff',
  },
  '&:disabled': {
    backgroundColor: colors.backgroundSoft,
    color: colors.textTertiary,
    boxShadow: 'none',
    opacity: 0.6,
  },
};

export const loadingSpinnerStyles = {
  color: colors.textWhite,
};
